// @flow

import {KB_CATEGORIES_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {
  get,
  del,
  createErrorHandler,
  postJson,
  patchJson,
  showForbiddenMessageHandler,
} from 'app/utils/request';
import {limitRequests} from 'app/utils/redux';
import type {ResponseType} from 'app/utils/redux_v2';
import type {KbCategoryType, KBCategoriesFetchParamsType} from 'app/kbs/types';
import {showNotification} from 'app/toasts/slice';

/* == KBActionType should contain all action types ==*/

export type KBCategoryActionType =
  | ReceiveKBCategoriesListType
  | ReceiveKBCategoryDetailActionType;

/* ==== fetch kb categories === */

export const RECEIVE_KB_CATEGORIES_LIST = 'RECEIVE_KB_CATEGORIES_LIST';

type ReceiveKBCategoriesListType = {
  type: 'RECEIVE_KB_CATEGORIES_LIST',
  query: KBCategoriesFetchParamsType,
  response: ResponseType<KbCategoryType>,
};
export const receiveKBCategoriesList = (
  query: KBCategoriesFetchParamsType,
  response: ResponseType<KbCategoryType>
): ReceiveKBCategoriesListType => ({
  type: RECEIVE_KB_CATEGORIES_LIST,
  query,
  response,
});

const requestKBCategories = (params: KBCategoriesFetchParamsType) =>
  get(KB_CATEGORIES_API_URL, params);

export const fetchKBCategories =
  (params: KBCategoriesFetchParamsType) => (dispatch: SimpleDispatch) =>
    requestKBCategories(params).then(
      (response) => dispatch(receiveKBCategoriesList(params, response)),
      createErrorHandler(gt('Unable to load the knowledge bases'), dispatch)
    );

export const fetchKBCategoriesForTree = limitRequests(
  (kb_id: number, parent_category_id: string | number) =>
    (dispatch: SimpleDispatch) => {
      return dispatch(fetchKBCategories({kb_id, parent_category_id}));
    }
);

/* === create kb category === */

export const createNewKBCategory =
  (data: $Shape<KbCategoryType>) => (dispatch: SimpleDispatch) =>
    // https://twitter.com/dan_abramov/status/770914221638942720
    postJson(KB_CATEGORIES_API_URL, data).then(
      (category: KbCategoryType) =>
        dispatch(receiveKBCategoryDetail(category)).category,
      createErrorHandler(
        gt('Could not create the Knowledge Base Category.'),
        dispatch
      )
    );

/* === fetch single kb category === */

export const RECEIVE_KB_CATEGORY_DETAIL = 'RECEIVE_KB_CATEGORY_DETAIL';

type ReceiveKBCategoryDetailActionType = {
  type: 'RECEIVE_KB_CATEGORY_DETAIL',
  category: KbCategoryType,
};

const receiveKBCategoryDetail = (
  category: KbCategoryType
): ReceiveKBCategoryDetailActionType => ({
  type: RECEIVE_KB_CATEGORY_DETAIL,
  category,
});

/* === update kb category === */

export const updateKBCategory =
  (url: string, data: $Shape<KbCategoryType>) => (dispatch: SimpleDispatch) =>
    patchJson(url, data).then(
      (kb) => dispatch(receiveKBCategoryDetail(kb)),
      createErrorHandler(
        gt('Unable to update the Knowledge Base Category'),
        dispatch,
        showForbiddenMessageHandler(true)
      )
    );

export const deleteKBCategory =
  (category: KbCategoryType) => (dispatch: SimpleDispatch) =>
    del(category.url).then(
      () =>
        dispatch(
          showNotification(gt('Category %s has been deleted.', category.name))
        ),
      createErrorHandler(
        gt('Unable to delete the category'),
        dispatch,
        showForbiddenMessageHandler(true)
      )
    );
